import {
  GenericVerificationProcessStubs,
  GovernmentIdVerificationProcessStubs,
} from 'dashboard/models/simulate/stubs';
import { ApiModel, ApiResource, PresetType } from 'lib/interfaces/api';
import { CheckName } from 'lib/verificationCheck';

export type SimulatePresetStubs =
  | GovernmentIdVerificationProcessStubs
  | GenericVerificationProcessStubs;

export interface BaseSimulatePreset {
  name: string;
  description?: string;
}

export interface SimulatePresetVerificationAttributes extends BaseSimulatePreset {
  'verification-type': string;
  stubs: SimulatePresetStubs;
}

export type SimulatePresetAttributes = SimulatePresetVerificationAttributes;

export type CreateSimulatePresetAttributes = SimulatePresetAttributes & {
  'preset-type': PresetType;
};

export type UpdateSimulatePresetAttributes = BaseSimulatePreset & {
  stubs: SimulatePresetStubs;
};

export type SimulatePresetType = ApiModel.SimulatePresetVerification;

export type SimulateCustomPresetVerification = ApiResource<
  SimulatePresetVerificationAttributes,
  {},
  ApiModel.SimulatePresetVerification
>;

export type SimulateCustomPreset = SimulateCustomPresetVerification;

export type StubsWithOptionalId = SimulatePresetVerificationAttributes & {
  id?: string;
};

export const VERIFICATION_TYPE_TO_PRESETS: Record<string, SimulatePresetVerificationAttributes[]> =
  {
    [ApiModel.VerificationTemplateVersionGovernmentId]: [
      {
        'verification-type': 'government_id',
        name: "Valid US Driver's License",
        description: "A captured US Driver's License that is valid.",
        stubs: {
          'verification-stubs': {
            'check-stubs': [],
          },
          'document-stubs': {
            kind: 'government_id',
            extractions: {
              birthdate: '1977-07-17',
              'identification-number': 'I1234562',
              'id-class': 'dl',
              'name-first': 'ALEXANDER J',
              'name-last': 'SAMPLE',
              'address-street-1': '600 CALIFORNIA STREET',
              'address-city': 'SAN FRANCISCO',
              'address-subdivision': 'CA',
              'address-postal-code': '94109',
              'issuing-authority': 'CA',
              'issuing-subdivision': 'CA',
              'country-code': 'US',
              sex: 'Male',
              'issue-date': '2024-01-01',
              'expiration-date': '2030-01-01',
            },
          },
        },
      },
      {
        'verification-type': 'government_id',
        name: 'Expired US Passport',
        description: 'A captured US Passport that is expired.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.IdExpiredDetection,
                status: 'failed',
                reasons: ['expired'],
              },
            ],
          },
          'document-stubs': {
            kind: 'government_id',
            extractions: {
              birthdate: '1977-07-17',
              'identification-number': 'I1234562',
              'id-class': 'pp',
              'name-first': 'ALEXANDER J',
              'name-last': 'SAMPLE',
              'country-code': 'US',
              sex: 'Male',
              'issue-date': '2015-01-01',
              'expiration-date': '2020-01-01',
            },
          },
        },
      },
    ],
    [ApiModel.VerificationTemplateVersionSelfie]: [
      {
        'verification-type': 'selfie',
        name: 'Valid Selfie',
        description: 'A captured Selfie that is valid.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [],
          },
        },
      },
      {
        'verification-type': 'selfie',
        name: 'Replicated Selfie',
        description: 'A captured Selfie that fails liveness detection.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.SelfieLivenessDetection,
                status: 'failed',
                reasons: ['replica_detected'],
              },
            ],
          },
        },
      },
    ],
    [ApiModel.VerificationTemplateVersionDatabase]: [
      {
        'verification-type': 'database',
        name: 'Valid Individual',
        description: 'The individual is valid.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [],
          },
        },
      },
      {
        'verification-type': 'database',
        name: 'Unknown Individual',
        description: 'The inputted first and last names fail to match an individual.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.DatabaseIdentityComparison,
                'match-requirement-stubs': [
                  { name: 'name_first', status: 'failed' },
                  { name: 'name_last', status: 'failed' },
                ],
              },
            ],
          },
        },
      },
      {
        'verification-type': 'database',
        name: 'Deceased Individual',
        description: 'The inputted details match that of a deceased individual.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.DatabaseDeceasedDetection,
                status: 'failed',
                reasons: ['person_is_deceased'],
              },
            ],
          },
        },
      },
    ],
    [ApiModel.VerificationTemplateVersionDocument]: [
      {
        'verification-type': 'document',
        name: 'Valid Document',
        description: 'A captured Document that is valid.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [],
          },
        },
      },
      {
        'verification-type': 'document',
        name: 'Tampered Document',
        description: 'A captured Document that is tampered.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.DocumentUnprocessableSubmissionDetection,
                status: 'failed',
                reasons: ['service_degraded'],
              },
            ],
          },
        },
      },
    ],
    [ApiModel.VerificationTemplateVersionDatabaseTin]: [
      {
        'verification-type': 'database_tin',
        name: 'Valid Individual',
        description: 'The individual is valid.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [],
          },
        },
      },
      {
        'verification-type': 'database_tin',
        name: 'Deceased Individual',
        description: 'The inputted details match that of a deceased individual.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.DatabaseTinDeceasedDetection,
                status: 'failed',
                reasons: ['deceased_match'],
              },
            ],
          },
        },
      },
    ],
    [ApiModel.VerificationTemplateVersionDatabaseStandard]: [
      {
        'verification-type': 'database_standard',
        name: 'Valid Individual',
        description: 'The individual is valid.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [],
          },
        },
      },
      {
        'verification-type': 'database_standard',
        name: 'Unknown Individual',
        description: 'The inputted details fail to match an individual.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.DatabaseStandardIdentityComparison,
                status: 'failed',
                reasons: ['insufficient_match'],
              },
            ],
          },
        },
      },
    ],
    [ApiModel.VerificationTemplateVersionDatabaseEcbsv]: [
      {
        'verification-type': 'database_ecbsv',
        name: 'Valid Individual',
        description: 'The individual is valid.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [],
          },
        },
      },
      {
        'verification-type': 'database_ecbsv',
        name: 'Unknown Individual',
        description: 'The inputted details fail to match an individual.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.DatabaseEcbsvRecordMatch,
                status: 'failed',
                reasons: ['insufficient_match'],
              },
            ],
          },
        },
      },
    ],
    [ApiModel.VerificationTemplateVersionAamva]: [
      {
        'verification-type': 'aamva',
        name: 'Valid Individual',
        description: 'The individual is valid.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [],
          },
        },
      },
      {
        'verification-type': 'aamva',
        name: 'Unknown Individual',
        description: 'The inputted identification number fails to match an individual.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.AamvaIdentificationNumberComparison,
                status: 'failed',
                reasons: ['insufficient_match'],
              },
            ],
          },
        },
      },
    ],
    [ApiModel.VerificationTemplateVersionDatabaseBusiness]: [
      {
        'verification-type': 'database_business',
        name: 'Valid Business',
        description: 'The business is valid.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [],
          },
        },
      },
      {
        'verification-type': 'database_business',
        name: 'Unknown Business',
        description: 'The name and registration number fail to match a business.',
        stubs: {
          'verification-stubs': {
            'check-stubs': [
              {
                name: CheckName.DatabaseBusinessIdentityComparison,
                'match-requirement-stubs': [
                  { name: 'business_name', status: 'failed' },
                  { name: 'registration_number', status: 'failed' },
                ],
              },
            ],
          },
        },
      },
    ],
  };
